import { useRef, useState, useEffect } from 'react';
import { Page } from 'react-pdf';
import { Loader2 } from 'lucide-react';

/**
 * Lazily renders a single react-pdf <Page> only when it scrolls near the viewport.
 * Large scanned PDFs used to mount every page (canvas + text layer) up-front, which
 * blocked the UI for many seconds. Here each page reserves its estimated height and
 * renders on demand via IntersectionObserver, so the viewer is interactive instantly
 * and navigation/scroll-tracking still work (data-page-number stays on the wrapper).
 * Once rendered a page stays mounted so scrolling back is instant.
 */
export const LazyPdfPage = ({
  pageNumber,
  width,
  scale = 1,
  rotation = 0,
  className = '',
  isActive = false,
  onRenderError,
  estimatedHeight = 850,
}) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            io.disconnect();
            break;
          }
        }
      },
      // Pre-render pages ~1.2 screens away so scrolling feels seamless.
      { root: null, rootMargin: '1200px 0px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible]);

  const estH = Math.round(estimatedHeight * (scale || 1));
  const ring = isActive ? 'ring-4 ring-blue-500 ring-offset-2' : '';

  return (
    <div
      ref={ref}
      data-page-number={pageNumber}
      className={`${className} ${ring}`.trim()}
      style={!visible ? { minHeight: estH, width: width || undefined } : undefined}
    >
      {visible ? (
        <Page
          pageNumber={pageNumber}
          width={width}
          scale={scale}
          rotate={rotation}
          renderTextLayer={true}
          renderAnnotationLayer={false}
          onRenderError={onRenderError}
          loading={
            <div style={{ minHeight: estH }} className="flex items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          }
        />
      ) : (
        <div
          style={{ minHeight: estH }}
          className="flex items-center justify-center bg-muted/10"
          data-testid={`lazy-page-placeholder-${pageNumber}`}
        >
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/40" />
        </div>
      )}
    </div>
  );
};

export default LazyPdfPage;
